<template>
  <div class="evaluation-report" v-loading="loading">
    <el-card shadow="never" class="head-card">
      <div class="head-bar">
        <div class="head-left">
          <el-button size="small" icon="el-icon-back" @click="$emit('back')">返回清单</el-button>
          <span class="mono head-id">{{ evalId }}</span>
        </div>
        <div class="head-right">
          <!-- 整份报告（本体 + 样例图 + 曲线）打成一个 zip 下载，浏览器直链 -->
          <el-button
            v-if="report"
            size="small"
            icon="el-icon-download"
            @click="downloadArchive"
          >打包下载</el-button>
        </div>
      </div>
      <div v-if="loadError" class="load-error">{{ loadError }}</div>
    </el-card>

    <template v-if="report">
      <!-- 概览：成绩与规模两栏 -->
      <el-card shadow="never" class="section-card">
        <div slot="header">概览</div>
        <div class="overview-cols">
          <div class="overview-col">
            <div class="ov-row">
              <span class="ov-label">总覆盖率</span>
              <span class="ov-main">
                <span class="ov-value ov-strong">{{ rateText(overall.coverage_rate) }}</span>
                <el-tag v-if="conclusion" :type="conclusionMeta(conclusion.state).tag" size="small">
                  {{ conclusionMeta(conclusion.state).label }}
                </el-tag>
                <span class="ov-sub">{{ overall.matched }}/{{ overall.gt }} 真值被覆盖</span>
              </span>
            </div>
            <div class="ov-row">
              <span class="ov-label">匹配实例平均覆盖率</span>
              <span class="ov-value">{{ rateText(overall.mean_matched_coverage) }}</span>
            </div>
            <div class="ov-row">
              <span class="ov-label">p05 覆盖率</span>
              <span class="ov-value">{{ rateText(overall.p05_coverage) }}</span>
            </div>
            <div v-if="failedItems.length" class="failed-chips">
              <span class="failed-chips-label">未达标项</span>
              <el-tag
                v-for="(item, index) in failedItems"
                :key="index"
                size="small"
                :type="item.scope === 'overall' ? 'danger' : 'warning'"
              >
                {{ item.name }} {{ rateText(item.coverage_rate) }}
              </el-tag>
            </div>
          </div>
          <div class="overview-col">
            <div class="ov-row">
              <span class="ov-label">预测/真值</span>
              <span class="ov-value">{{ diagnostics.predictions }}/{{ overall.gt }}</span>
            </div>
            <div class="ov-row">
              <span class="ov-label">未匹配预测</span>
              <span class="ov-value">{{ diagnostics.unmatched_predictions }}</span>
            </div>
            <div class="ov-row">
              <span class="ov-label">测试集</span>
              <span class="ov-value">
                <span class="mono">{{ dash(identity.dataset_ref) }}</span>
                <span v-if="identity.split" class="ov-sub"> · {{ splitLabel(identity.split) }}</span>
              </span>
            </div>
            <div class="ov-row">
              <span class="ov-label">模型</span>
              <span class="ov-value">
                {{ dash(identity.model_name) }}
                <span v-if="identity.model_id" class="ov-sub mono"> · {{ identity.model_id }}</span>
              </span>
            </div>
            <div class="ov-row">
              <span class="ov-label">完成时间</span>
              <span class="ov-value">{{ finishedAt }}</span>
            </div>
          </div>
        </div>
      </el-card>

      <!-- 覆盖率：业务组与类别并排，越差排越前 -->
      <div class="charts-row">
        <el-card v-if="groupChartRows.length" shadow="never" class="section-card chart-card">
          <div slot="header">业务组覆盖率</div>
          <e-chart :option="groupChartOption" :height="groupChartHeight" @chart-click="onCoverageClick" />
        </el-card>
        <el-card v-if="classChartRows.length" shadow="never" class="section-card chart-card">
          <div slot="header">类别覆盖率</div>
          <e-chart :option="classChartOption" :height="classChartHeight" @chart-click="onCoverageClick" />
        </el-card>
      </div>
      <div v-if="failedItems.length" class="jump-hint">
        点击图中低于阈值的条目，可跳到附录里的对应明细
      </div>

      <!-- 未匹配预测与样例图并排：多检那批左真值、右预测成对 -->
      <div class="charts-row">
        <el-card v-if="hasUnmatched" shadow="never" class="section-card chart-card">
          <div slot="header">未匹配预测</div>
          <div class="fact-line">
            预测 {{ diagnostics.predictions }} 个 · 真值 {{ overall.gt }} 个 · 未匹配
            {{ diagnostics.unmatched_predictions }} 个
          </div>
          <div v-if="extraPairs.length" class="sample-grid samples-scroll">
            <div
              v-for="pair in extraPairs"
              :key="pair.pred"
              class="sample-cell"
              :class="{ 'sample-cell-pair': pair.truth }"
            >
              <div v-if="pair.truth" class="sample-pair-images">
                <div class="sample-pair-side">
                  <span class="sample-pair-tag">真值</span>
                  <el-image
                    :src="objectUrl(pair.truth)"
                    :preview-src-list="pairUrls(pair)"
                    lazy
                    fit="cover"
                    class="sample-image"
                  />
                </div>
                <div class="sample-pair-side">
                  <span class="sample-pair-tag">预测</span>
                  <el-image
                    :src="objectUrl(pair.pred)"
                    :preview-src-list="pairUrls(pair)"
                    lazy
                    fit="cover"
                    class="sample-image"
                  />
                </div>
              </div>
              <el-image
                v-else
                :src="objectUrl(pair.pred)"
                :preview-src-list="categoryUrls('extra')"
                lazy
                fit="cover"
                class="sample-image"
              />
              <div class="sample-name mono" :title="pair.pred">{{ baseName(pair.pred) }}</div>
            </div>
          </div>
          <div v-else class="cell-sub section-gap">没有样例图</div>
        </el-card>

        <!-- 样例：漏检与低置信两组（多检那批在左边卡里） -->
        <el-card shadow="never" class="section-card chart-card">
          <div slot="header">
            样例图
            <span class="cell-sub header-count">{{ sampleCountsText }}</span>
          </div>
          <div class="samples-scroll">
            <div v-for="(category, index) in SAMPLE_CATEGORIES" :key="category.key" class="sample-block">
              <div class="sub-title" :class="{ first: index === 0 }">{{ category.label }}</div>
              <div v-if="(samples[category.key] || []).length" class="sample-grid">
                <div v-for="rel in samples[category.key]" :key="rel" class="sample-cell">
                  <el-image
                    :src="objectUrl(rel)"
                    :preview-src-list="categoryUrls(category.key)"
                    lazy
                    fit="cover"
                    class="sample-image"
                  />
                  <div class="sample-name mono" :title="rel">{{ baseName(rel) }}</div>
                </div>
              </div>
              <div v-else class="cell-sub">没有</div>
            </div>
          </div>
        </el-card>
      </div>

      <!-- 附录：要逐项查数字时翻的折叠区 -->
      <el-card shadow="never" class="section-card">
        <div slot="header">
          <span class="collapse-toggle" @click="detailOpen = !detailOpen">
            <i :class="detailOpen ? 'el-icon-arrow-down' : 'el-icon-arrow-right'" />
            附录
          </span>
        </div>
        <div v-show="detailOpen">
          <div class="sub-title first">未匹配预测置信度分布</div>
          <e-chart v-if="hasUnmatched" :option="histogramOption" height="200px" />
          <div v-else class="cell-sub">没有</div>
          <template v-if="confusionRows.length">
            <div class="sub-title">组内类别混淆</div>
            <el-table :data="confusionRows" size="mini" border style="width:100%">
              <el-table-column prop="group" label="组" width="110" />
              <el-table-column prop="from" label="真值类别" min-width="110" />
              <el-table-column prop="to" label="判成" min-width="110" />
              <el-table-column prop="count" label="实例数" width="80" />
            </el-table>
          </template>
          <template v-if="failedItems.length">
            <div class="sub-title">未达标明细</div>
            <div
              v-for="(item, index) in failedItems"
              :key="index"
              ref="failedItems"
              class="failed-item"
              :class="{ 'item-flash': flash.index === index && !flash.name }"
            >
              <div class="failed-head">
                <el-tag size="mini" :type="item.scope === 'overall' ? 'danger' : 'warning'">
                  {{ item.scope === 'overall' ? '整体' : '组' }}
                </el-tag>
                <span class="failed-name">{{ item.name }}</span>
                <span class="cell-sub">
                  覆盖率 {{ rateText(item.coverage_rate) }}（{{ item.matched }}/{{ item.gt }}）
                </span>
              </div>
              <el-table
                :data="item.classes"
                size="mini"
                border
                style="width:100%"
                :cell-style="cellStyleFor(index)"
              >
                <el-table-column type="expand">
                  <template slot-scope="{ row }">
                    <div class="frames-box">
                      <span class="cell-sub">
                        共 {{ row.frames_total }} 帧{{
                          row.frames_total > row.frames.length ? `，只列出前 ${row.frames.length} 帧` : ''
                        }}{{
                          frameImageCount(row) ? ` · 前 ${frameImageCount(row)} 帧有标注图，点图放大` : ''
                        }}
                      </span>
                      <div class="frames-list">
                        <div v-for="frame in row.frames" :key="frame" class="frame-row">
                          <el-image
                            v-if="frameSample(frame)"
                            :src="objectUrl(frameSample(frame))"
                            :preview-src-list="frameUrls(row)"
                            lazy
                            fit="cover"
                            class="frame-thumb"
                          />
                          <div class="mono frame-name" :title="frame">{{ frame }}</div>
                        </div>
                      </div>
                    </div>
                  </template>
                </el-table-column>
                <el-table-column prop="name" label="类别" min-width="110" />
                <el-table-column label="覆盖率" width="90">
                  <template slot-scope="{ row }">{{ rateText(row.coverage_rate) }}</template>
                </el-table-column>
                <el-table-column label="匹配 / 真值" width="100">
                  <template slot-scope="{ row }">{{ row.matched }} / {{ row.gt }}</template>
                </el-table-column>
                <el-table-column label="失败帧" width="80">
                  <template slot-scope="{ row }">{{ row.frames_total }}</template>
                </el-table-column>
              </el-table>
            </div>
          </template>

          <div class="sub-title">精度指标</div>
          <div class="metric-grid">
            <div v-for="(value, key) in metrics" :key="key" class="metric-cell">
              <el-tooltip :content="String(key)" placement="top">
                <span class="metric-key">{{ metricLabel(key) }}</span>
              </el-tooltip>
              <span class="metric-value">{{ metricValue(value) }}</span>
            </div>
          </div>
          <template v-if="apRows.length">
            <div class="sub-title">逐类 AP</div>
            <el-table :data="apRows" size="mini" border style="width:100%">
              <el-table-column prop="name" label="类别" min-width="110" />
              <el-table-column label="AP50（框）">
                <template slot-scope="{ row }">{{ apText(row.box, 'ap50') }}</template>
              </el-table-column>
              <el-table-column label="AP50-95（框）">
                <template slot-scope="{ row }">{{ apText(row.box, 'ap50-95') }}</template>
              </el-table-column>
              <el-table-column label="AP50（掩码）">
                <template slot-scope="{ row }">{{ apText(row.seg, 'ap50') }}</template>
              </el-table-column>
              <el-table-column label="AP50-95（掩码）">
                <template slot-scope="{ row }">{{ apText(row.seg, 'ap50-95') }}</template>
              </el-table-column>
            </el-table>
          </template>

          <div class="sub-title">数据检查</div>
          <el-descriptions :column="3" size="small" border>
            <el-descriptions-item label="图像数">{{ dataset.images }}</el-descriptions-item>
            <el-descriptions-item label="有标注帧">{{ dataset.labeled_images }}</el-descriptions-item>
            <el-descriptions-item label="真值实例数">{{ dataset.instances }}</el-descriptions-item>
            <el-descriptions-item label="空标注帧">{{ dataset.empty_label_files }}</el-descriptions-item>
            <el-descriptions-item label="缺标注文件">{{ dataset.missing_label_files }}</el-descriptions-item>
            <el-descriptions-item label="类别表一致">
              <el-tag :type="dataset.classes_txt_matches_data_yaml ? 'success' : 'danger'" size="mini">
                {{ dataset.classes_txt_matches_data_yaml ? '是' : '否' }}
              </el-tag>
            </el-descriptions-item>
          </el-descriptions>
          <div v-if="(dataset.classes_without_truth || []).length" class="fact-line warn">
            这些类别这次没有真值，未评估：
            <span v-for="name in dataset.classes_without_truth" :key="name" class="mono">{{ name }} </span>
          </div>
          <div class="sub-title">评估信息</div>
          <el-descriptions :column="2" size="small" border>
            <el-descriptions-item label="训练集">
              <template v-if="trainingSource">
                <span class="mono">{{ trainingSource.dataset_ref }}</span>
                <span class="cell-sub"> · {{ splitLabel(trainingSource.split) }}</span>
              </template>
              <span v-else>—</span>
            </el-descriptions-item>
            <el-descriptions-item label="评估方案">
              {{ dash(profile.name) }}
              <el-tooltip v-if="profile.digest" :content="`内容指纹 ${profile.digest}`" placement="top">
                <i class="el-icon-info cell-sub" />
              </el-tooltip>
            </el-descriptions-item>
          </el-descriptions>
          <div class="sub-title">推理参数</div>
          <div class="metric-grid">
            <div v-for="(value, key) in contract" :key="key" class="metric-cell">
              <el-tooltip :content="String(key)" placement="top">
                <span class="metric-key">{{ CONTRACT_LABELS[key] || key }}</span>
              </el-tooltip>
              <span class="metric-value">{{ value }}</span>
            </div>
          </div>
        </div>
      </el-card>
    </template>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import EChart from './EChart.vue';
import {
  fetchEvaluation,
  evaluationObjectUrl,
  evaluationArchiveUrl,
} from '../api/blissrose/evaluation';
import {
  conclusionMeta,
  rateText,
  dash,
  rateColor,
  metricLabel,
  splitLabel,
  coverageMarkLine,
  CONTRACT_LABELS,
} from '../utils/evalMeta';

/** 样例图卡里的两组（多检那批在「未匹配预测」卡里） */
const SAMPLE_CATEGORIES = [
  { key: 'missed', label: '漏检' },
  { key: 'low_conf', label: '低置信' },
];

/** 覆盖率轴统一按百分比显示 */
function percentAxis(value) {
  return `${Math.round(value * 100)}%`;
}

/** 一份评估报告：从结论到证据逐层往下；附录折叠，与前面重合的内容不重复 */
export default {
  name: 'EvaluationReport',

  components: { EChart },

  props: {
    evalId: { type: String, required: true },
  },

  data() {
    return {
      SAMPLE_CATEGORIES,
      CONTRACT_LABELS,
      loading: false,
      loadError: '',
      detail: null,
      detailOpen: false,
      /** 从图上跳过来时高亮的明细位置：index 为 failed_items 下标，name 为空闪整条 */
      flash: { index: -1, name: '' },
      flashTimer: null,
    };
  },

  computed: {
    report() {
      return this.detail ? this.detail.report : null;
    },
    source() {
      return this.detail ? this.detail.source : null;
    },
    conclusion() {
      return this.detail ? this.detail.conclusion : null;
    },
    trainingSource() {
      return this.detail ? this.detail.training_source : null;
    },
    identity() {
      return (this.report && this.report.identity) || {};
    },
    profile() {
      return this.identity.profile || {};
    },
    contract() {
      return (this.report && this.report.contract) || {};
    },
    business() {
      return (this.report && this.report.business) || { overall: {} };
    },
    overall() {
      return this.business.overall || {};
    },
    threshold() {
      if (this.conclusion && typeof this.conclusion.threshold === 'number') {
        return this.conclusion.threshold;
      }
      return typeof this.business.coverage_threshold === 'number' ? this.business.coverage_threshold : null;
    },
    dataset() {
      return (this.report && this.report.dataset) || {};
    },
    samples() {
      return (this.report && this.report.samples) || {};
    },
    /** 未匹配预测那批样例：extra 与 extra_gt 同帧同序成对（老报告只出预测版一张） */
    extraPairs() {
      const predictions = this.samples.extra || [];
      const truths = this.samples.extra_gt || [];
      return predictions.map((pred, index) => ({ pred, truth: truths[index] || null }));
    },
    /** 样例图卡头部的计数：漏检 / 低置信各多少张 */
    sampleCountsText() {
      return SAMPLE_CATEGORIES
        .map(category => `${category.label} ${(this.samples[category.key] || []).length}`)
        .join(' · ');
    },
    ultralytics() {
      return (this.report && this.report.ultralytics) || {};
    },
    metrics() {
      return this.ultralytics.metrics || {};
    },
    /** 诊断三件套在早期报告里可能缺，给全默认值 */
    diagnostics() {
      return {
        predictions: 0,
        pred_per_gt: 0,
        unmatched_predictions: 0,
        unmatched_conf_histogram: {},
        ...(this.business.diagnostics || {}),
      };
    },
    failedItems() {
      return (this.conclusion || {}).failed_items || [];
    },
    /** 图上条目 → 附录明细的跳转表：组名 → 明细位置；类名 → 所属组的明细（找不到的不进表） */
    failedJumpTargets() {
      const groups = {};
      const classes = {};
      const fill = (item, index) => {
        (item.classes || []).forEach(cls => {
          if (cls.name && !classes[cls.name]) classes[cls.name] = { index, className: cls.name };
        });
      };
      this.failedItems.forEach((item, index) => {
        if (item.scope !== 'overall' && item.name) groups[item.name] = { index, className: '' };
      });
      this.failedItems.forEach((item, index) => {
        if (item.scope !== 'overall') fill(item, index);
      });
      this.failedItems.forEach((item, index) => {
        if (item.scope === 'overall') fill(item, index);
      });
      return { groups, classes };
    },
    finishedAt() {
      return this.formatTime((this.source && this.source.eval_finished_at) || '') || this.formatTime(this.report.generated_at) || '—';
    },

    // ====== 覆盖率图 ======
    groupChartRows() {
      return Object.values(this.business.groups || {})
        .map(row => ({
          name: row.group,
          rate: typeof row.coverage_rate === 'number' ? row.coverage_rate : null,
          gt: row.gt,
          matched: row.matched,
          mean: row.mean_matched_coverage,
          p05: row.p05_coverage,
        }))
        .sort((a, b) => {
          if (a.rate === null && b.rate === null) return a.name.localeCompare(b.name);
          if (a.rate === null) return 1;
          if (b.rate === null) return -1;
          return a.rate - b.rate;
        });
    },
    classChartRows() {
      return Object.entries(this.business.per_class || {})
        .map(([name, row]) => ({
          name,
          rate: typeof row.coverage_rate === 'number' ? row.coverage_rate : null,
          gt: row.gt,
          matched: row.matched,
          mean: row.mean_matched_coverage,
          p05: row.p05_coverage,
        }))
        .sort((a, b) => {
          if (a.rate === null && b.rate === null) return a.name.localeCompare(b.name);
          if (a.rate === null) return 1;
          if (b.rate === null) return -1;
          return a.rate - b.rate;
        });
    },
    groupChartHeight() {
      return `${Math.max(150, this.groupChartRows.length * 34 + 46)}px`;
    },
    classChartHeight() {
      return `${Math.min(760, Math.max(170, this.classChartRows.length * 26 + 46))}px`;
    },
    groupChartOption() {
      return this.coverageOption(this.groupChartRows, 'group');
    },
    classChartOption() {
      return this.coverageOption(this.classChartRows, 'class');
    },

    // ====== 未匹配预测直方图 ======
    hasUnmatched() {
      return this.diagnostics.unmatched_predictions > 0;
    },
    histogramOption() {
      const entries = Object.entries(this.diagnostics.unmatched_conf_histogram || {})
        .sort((a, b) => parseFloat(a[0]) - parseFloat(b[0]));
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: params => `${params[0].name}：${params[0].value} 个未匹配预测`,
        },
        grid: { left: 8, right: 30, top: 28, bottom: 8, containLabel: true },
        xAxis: {
          type: 'category',
          name: '置信度',
          nameLocation: 'end',
          nameGap: 6,
          nameTextStyle: { color: '#909399', fontSize: 11, align: 'right' },
          data: entries.map(([bucket]) => bucket),
          axisTick: { show: false },
          axisLine: { lineStyle: { color: '#dcdfe6' } },
          axisLabel: { color: '#909399', fontSize: 11 },
        },
        yAxis: {
          type: 'value',
          name: '框数',
          nameLocation: 'end',
          nameGap: 10,
          nameTextStyle: { color: '#909399', fontSize: 11, align: 'left' },
          minInterval: 1,
          axisLabel: { color: '#909399', fontSize: 11 },
          splitLine: { show: false },
        },
        series: [
          {
            type: 'bar',
            barMaxWidth: 18,
            data: entries.map(([, count]) => count),
            itemStyle: { color: '#409eff', borderRadius: [2, 2, 0, 0] },
            label: {
              show: true,
              position: 'top',
              color: '#909399',
              fontSize: 10,
              formatter: params => (params.value > 0 ? String(params.value) : ''),
            },
          },
        ],
      };
    },

    // ====== 附录里的表 ======
    /** 逐帧附图的索引：帧名（去目录去扩展名）→ samples/failed 里的相对路径 */
    failedSampleByStem() {
      const map = {};
      (this.samples.failed || []).forEach(rel => {
        map[this.baseName(rel)] = rel;
      });
      return map;
    },
    /** 只留判成别的类别的那些 */
    confusionRows() {
      const rows = [];
      const confusion = this.business.group_confusion || {};
      Object.keys(confusion).forEach(group => {
        Object.keys(confusion[group]).forEach(from => {
          Object.keys(confusion[group][from]).forEach(to => {
            if (from !== to) rows.push({ group, from, to, count: confusion[group][from][to] });
          });
        });
      });
      return rows;
    },
    /** box 与 seg 两份逐类 AP 按类名并起来，一边缺的那几列写「—」 */
    apRows() {
      const perClassAp = this.ultralytics.per_class_ap;
      if (!perClassAp) return [];
      const names = new Set([...Object.keys(perClassAp.box || {}), ...Object.keys(perClassAp.seg || {})]);
      return [...names].sort().map(name => ({
        name,
        box: (perClassAp.box || {})[name] || null,
        seg: (perClassAp.seg || {})[name] || null,
      }));
    },
  },

  watch: {
    evalId: {
      immediate: true,
      handler() {
        this.load();
      },
    },
  },

  beforeDestroy() {
    window.clearTimeout(this.flashTimer);
  },

  methods: {
    conclusionMeta,
    rateText,
    dash,
    metricLabel,
    splitLabel,

    async load() {
      this.loading = true;
      this.loadError = '';
      this.detail = null;
      this.detailOpen = false;
      // 换报告时清掉上一份的跳转高亮
      this.flash = { index: -1, name: '' };
      window.clearTimeout(this.flashTimer);
      this.flashTimer = null;
      try {
        this.detail = (await fetchEvaluation(this.evalId)).data;
      } catch (err) {
        // 提示由响应拦截器统一弹出，页面留一行错误
        this.loadError = err.message || '报告加载失败，稍后重试';
      } finally {
        this.loading = false;
      }
    },

    /** 组/类两个粒度共用的条形图设定：越差排越前；低于阈值且有明细的条目可点击跳转 */
    coverageOption(rows, kind) {
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: params => {
            const item = params[0];
            const row = item.data || {};
            if (row.noTruth) return `${item.name}：无真值，未评估`;
            return `${item.name}<br/>覆盖率 ${rateText(item.value)}（${row.matched}/${row.gt} 真值被覆盖）`
              + `<br/>平均覆盖率 ${rateText(row.mean)}<br/>p05 覆盖率 ${rateText(row.p05)}`;
          },
        },
        // top 要留出阈值线标签（画在网格上沿之上）
        grid: { left: 8, right: 62, top: 26, bottom: 8, containLabel: true },
        xAxis: {
          type: 'value',
          max: 1,
          min: 0,
          axisLabel: { formatter: percentAxis, color: '#909399', fontSize: 11 },
          splitLine: { show: false },
        },
        yAxis: {
          type: 'category',
          inverse: true,
          data: rows.map(row => row.name),
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: '#303133', fontSize: 12 },
        },
        series: [
          {
            type: 'bar',
            barMaxWidth: 14,
            data: rows.map(row => {
              if (row.rate === null) {
                return { value: 0, noTruth: true, itemStyle: { color: '#c0c4cc' } };
              }
              const target = this.jumpTargetOf(kind, row.name, row.rate);
              return {
                value: row.rate,
                gt: row.gt,
                matched: row.matched,
                mean: row.mean,
                p05: row.p05,
                jumpIndex: target ? target.index : null,
                jumpClass: target ? target.className : '',
                cursor: target ? 'pointer' : null,
                itemStyle: { color: rateColor(row.rate, this.threshold) },
              };
            }),
            label: {
              show: true,
              position: 'right',
              color: '#909399',
              fontSize: 10,
              formatter: params => (params.data && params.data.noTruth ? '无真值' : rateText(params.value)),
            },
            markLine: typeof this.threshold === 'number'
              ? coverageMarkLine([{ threshold: this.threshold, label: `阈值 ${rateText(this.threshold)}` }])
              : undefined,
          },
        ],
      };
    },

    // ====== 图上条目 → 附录明细的跳转 ======

    /** 图上条目有没有可跳的明细：低于阈值且附录里有对应条目才算 */
    jumpTargetOf(kind, name, rate) {
      if (typeof this.threshold !== 'number' || rate >= this.threshold) return null;
      const table = kind === 'group' ? this.failedJumpTargets.groups : this.failedJumpTargets.classes;
      return table[name] || null;
    },

    /** 覆盖率图上的点击：只认带跳转目标的条目，其余条目的点击忽略 */
    onCoverageClick(params) {
      const data = params.data || {};
      if (typeof data.jumpIndex !== 'number') return;
      this.jumpToFailed(data.jumpIndex, data.jumpClass || '');
    },

    /** 跳到附录里对应的未达标明细：先展开附录，再滚到那一条并闪一会儿；带类名的落在对应行 */
    jumpToFailed(index, className) {
      this.detailOpen = true;
      this.flash = { index, name: className };
      this.$nextTick(() => {
        const el = this.$refs.failedItems && this.$refs.failedItems[index];
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.clearTimeout(this.flashTimer);
        this.flashTimer = window.setTimeout(() => {
          this.flash = { index: -1, name: '' };
        }, 2400);
      });
    },

    /** 被跳中的那一行给个底色；落点是 td（cell-style 的施加处） */
    cellStyleFor(index) {
      return ({ row }) => (
        this.flash.index === index && this.flash.name && row.name === this.flash.name
          ? { backgroundColor: '#fdf6ec' }
          : null
      );
    },

    /** 整份报告打包下载：走一次性的直链，交浏览器自己落盘 */
    downloadArchive() {
      const link = document.createElement('a');
      link.href = evaluationArchiveUrl(this.evalId);
      link.download = '';
      document.body.appendChild(link);
      link.click();
      link.remove();
    },

    // ====== 展示辅助 ======

    objectUrl(relPath) {
      return evaluationObjectUrl(this.evalId, relPath);
    },

    categoryUrls(key) {
      return (this.samples[key] || []).map(rel => this.objectUrl(rel));
    },

    /** 成对样例的放大列表：真值在前、预测在后，点哪张都能左右翻 */
    pairUrls(pair) {
      if (!pair.truth) return this.categoryUrls('extra');
      return [this.objectUrl(pair.truth), this.objectUrl(pair.pred)];
    },

    /** 帧在 samples/failed 里的标注图（按帧名对上），没有就 null */
    frameSample(frame) {
      return this.failedSampleByStem[this.baseName(frame)] || null;
    },

    /** 这一类的明细帧里存了几张标注图 */
    frameImageCount(row) {
      return (row.frames || []).filter(frame => this.frameSample(frame)).length;
    },

    /** 放大列表：这一类里有图的帧，点哪张都能左右翻 */
    frameUrls(row) {
      return (row.frames || [])
        .map(frame => this.frameSample(frame))
        .filter(Boolean)
        .map(rel => this.objectUrl(rel));
    },

    baseName(relPath) {
      const name = String(relPath).split('/').pop() || relPath;
      return name.replace(/\.[^.]+$/, '');
    },

    /** 指标数字：大数留一位、小数留四位 */
    metricValue(value) {
      if (typeof value !== 'number') return dash(value);
      return Math.abs(value) >= 100 ? value.toFixed(1) : value.toFixed(4);
    },

    apText(row, key) {
      if (!row || typeof row[key] !== 'number') return '—';
      return row[key].toFixed(4);
    },

    formatTime(value) {
      return value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '';
    },
  },
};
</script>

<style scoped>
.head-card {
  margin-bottom: 16px;
}

.section-card {
  margin-bottom: 16px;
}

.head-bar {
  display: flex;
  align-items: center;
}

.head-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.head-id {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

/* 右侧动作区：下载按钮贴右，不多占左侧信息的位置 */
.head-right {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
}

/* 并排的两张图卡 */
.charts-row {
  display: flex;
  align-items: stretch;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.charts-row .chart-card {
  flex: 1 1 360px;
  min-width: 0;
  margin-bottom: 0;
}

.header-count {
  margin-left: 10px;
}

.load-error {
  margin-top: 8px;
  color: #f56c6c;
  font-size: 13px;
}

/* 概览两栏 */
.overview-cols {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 48px;
}

.overview-col {
  flex: 1 1 320px;
  min-width: 0;
}

.ov-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  padding: 7px 0;
  border-bottom: 1px dashed #ebeef5;
  font-size: 13px;
}

.ov-row:last-child {
  border-bottom: none;
}

.ov-label {
  color: #606266;
  white-space: nowrap;
}

.ov-main {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.ov-value {
  color: #303133;
  font-weight: 600;
  text-align: right;
}

.ov-strong {
  font-size: 22px;
}

.ov-sub {
  font-size: 12px;
  color: #909399;
  font-weight: 400;
}

.failed-chips {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.failed-chips-label {
  font-size: 12px;
  color: #909399;
}

.fact-line {
  font-size: 13px;
  color: #606266;
  line-height: 1.8;
  margin-top: 6px;
}

.fact-line.warn {
  color: #e6a23c;
}

.collapse-toggle {
  cursor: pointer;
  user-select: none;
}

.section-gap {
  margin-top: 14px;
}

/* 图上可跳条目的提示（负上边距贴住上面那排图） */
.jump-hint {
  margin: -6px 0 16px 2px;
  font-size: 12px;
  color: #909399;
}

.failed-item {
  margin-top: 12px;
  border-radius: 4px;
  transition: background-color 0.4s;
}

/* 跳过来时整条闪一下；类条目只闪行（见 cellStyleFor） */
.failed-item.item-flash {
  background-color: #fdf6ec;
}

.failed-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.failed-name {
  font-weight: 600;
  color: #303133;
}

.frames-box {
  padding: 4px 8px;
}

.frames-list {
  max-height: 240px;
  overflow: auto;
  margin-top: 4px;
  font-size: 12px;
  color: #606266;
  line-height: 1.6;
}

/* 有图的帧留一列缩略图 */
.frame-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.frame-thumb {
  width: 56px;
  height: 42px;
  border: 1px solid #ebeef5;
  border-radius: 3px;
  flex: none;
  display: block;
}

.frame-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 样例图区域：图多时卡内滚动 */
.samples-scroll {
  max-height: 340px;
  overflow-y: auto;
}

.sub-title {
  font-weight: 600;
  color: #303133;
  font-size: 13px;
  margin: 14px 0 6px;
}

.sub-title.first {
  margin-top: 0;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 6px 16px;
}

.metric-cell {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  border-bottom: 1px dashed #ebeef5;
  padding: 3px 0;
  font-size: 13px;
}

.metric-key {
  color: #606266;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.metric-value {
  color: #303133;
  font-weight: 600;
}

.sample-block {
  margin-top: 8px;
}

.sample-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.sample-cell {
  width: 104px;
}

.sample-image {
  width: 104px;
  height: 78px;
  border: 1px solid #ebeef5;
  border-radius: 3px;
  display: block;
}

.sample-name {
  font-size: 11px;
  color: #909399;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: 2px;
}

/* 未匹配预测的成对样例：左真值右预测并排 */
.sample-cell-pair {
  width: 214px;
}

.sample-pair-images {
  display: flex;
  gap: 6px;
}

.sample-pair-side {
  display: flex;
  flex-direction: column;
}

.sample-pair-tag {
  font-size: 11px;
  color: #909399;
  margin-bottom: 2px;
}

.mono {
  font-family: Consolas, Monaco, monospace;
}

.cell-sub {
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}
</style>
